/**
 * Project context (tab „Context”) — /api/projects/:id/context.
 *
 * The consolidated document lives in the KB (projects/<slug>/context.md); the
 * journal entries are the raw additions (from the context chat, a direct
 * note, or voice). Mirrors backend src/routes/projects.context.ts.
 */
import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

export type ProjectContextSource = 'CHAT' | 'MANUAL' | 'VOICE';

export interface ProjectContextDocument {
  markdown: string;
  updatedAt: string | null;
  kbPath: string;
  exists: boolean;
}

export interface ProjectContextEntry {
  id: string;
  content: string;
  source: ProjectContextSource;
  createdAt: string;
  conversationId: string | null;
  author: { id: string; name: string } | null;
}

export interface ProjectContextPayload {
  document: ProjectContextDocument;
  entries: ProjectContextEntry[];
}

export interface AddContextEntryResult {
  entry: ProjectContextEntry;
  document: ProjectContextDocument;
  consolidation: 'ai' | 'appended';
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function getProjectContext(projectId: string): Promise<ProjectContextPayload> {
  const res = await apiFetch<Envelope<ProjectContextPayload>>(
    `/api/projects/${projectId}/context`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function saveProjectContextDocument(
  projectId: string,
  markdown: string,
): Promise<ProjectContextDocument> {
  const res = await apiFetch<Envelope<{ document: ProjectContextDocument }>>(
    `/api/projects/${projectId}/context`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ markdown }),
    },
  );
  return res.data.document;
}

export async function addProjectContextEntry(
  projectId: string,
  content: string,
): Promise<AddContextEntryResult> {
  const res = await apiFetch<Envelope<AddContextEntryResult>>(
    `/api/projects/${projectId}/context/entries`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ content }),
    },
  );
  return res.data;
}
